<template>
  <section id="ebooks" class="products-section">
    <h2 class="section-title">E-BOOKS & CURSOS</h2>
    
    <div class="carousel-wrapper">
      <button v-show="!isAtStart && books.length > 0" class="nav-btn prev" 
            @click="scroll('left')">
            ‹
      </button>
      
      <div v-if="loading" class="loading-state">Carregando catálogo...</div>

      <div v-else class="product-grid" ref="carouselRef">
        <div v-for="book in books" :key="book.id" class="product-card">
          
          <router-link :to="{ name: 'LivroDetalhes', params: { id: book.id }}" class="card-link">
            <div class="card-image">
              <img :src="book.image?.url" :alt="book.title">
            </div>
            
            <div class="card-info">
              <h3 class="product-title">{{ book.title }}</h3>
              
              <p class="product-price">
                R$ {{ (book.price || 0).toFixed(2).replace('.', ',') }}
              </p>
            </div>
          </router-link> 
        </div> 
      </div>

      <button v-show="!isAtEnd && books.length > 0" class="nav-btn next" 
            @click="scroll('right')">
            ›
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';

const endpoint = process.env.VUE_APP_HYGRAPH_URL;
const isAtStart = ref(true); // Controla se o botão 'prev' aparece
const isAtEnd = ref(false);   // Controla se o botão 'next' aparece
// Definição do tipo para TypeScript
interface Book {
  id: string;
  title: string;
  price: number;
  image: { url: string };
  link: string;
}

const books = ref<Book[]>([]);
const loading = ref(true);
const carouselRef = ref<HTMLElement | null>(null);


  // Função para garantir que as capas dos livros tenham o mesmo tamanho e não estorem
const optimizeThumb = (url: string) => {
  if (!url) return 'https://via.placeholder.com/300x400?text=Sem+Capa'; // Fallback
  return url.replace('https://media.graphassets.com/', 'https://media.graphassets.com/resize=width:400,height:550,fit:crop/');
};

// Função para buscar dados do Hygraph
  const fetchBooks = async () => {

    const query = `
      query GetCursos {
        courses {
          id
          title
          price
          image {
            url
          }
          link
        }
      }
    `;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query })
      });
      const { data } = await response.json();
      
      if (data && data.courses) {
        books.value = data.courses.map((book: any) => ({
          ...book,
          image: { url: optimizeThumb(book.image?.url) },
          link: book.link
        }));

      }
    } catch (error) {
      console.error("Erro ao carregar dados do Hygraph:", error);
    } finally {
      loading.value = false;
    }
  };

  const checkScrollLimits = () => {
    if (carouselRef.value) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.value;
      
      isAtStart.value = scrollLeft <= 0;
      // O '+1' ajuda a evitar erros de arredondamento em alguns navegadores
      isAtEnd.value = scrollLeft + clientWidth >= scrollWidth - 1;
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (carouselRef.value) {
      // Definimos o pulo baseado na largura de um card (280px + 20px de gap)
      const scrollAmount = 300; 
      
      carouselRef.value.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });

      // Aguarda a animação terminar para checar os novos limites
      setTimeout(checkScrollLimits, 400);
    }
  };

  onMounted(() => {
    fetchBooks();
    if (carouselRef.value) {
      carouselRef.value.addEventListener('scroll', checkScrollLimits);
    }
  });

</script>

<style scoped>

.section-title{
  color: #333333;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 40px;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-align: center;
  padding-top: 2%;
}

/* Adicione apenas este estilo extra para o estado de loading */
.loading-state {
  color: #c0c0c0;
  padding: 40px;
  width: 100%;
}

/* Garante que o grid não esconda os botões */
.product-grid {
  display: flex;
  gap: 20px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 20px 0;
  scrollbar-width: none; /* Esconde scrollbar no Firefox */
  width: 100%;
}

.product-grid::-webkit-scrollbar {
  display: none; /* Esconde scrollbar no Chrome/Safari */
}

.product-card {
  min-width: 280px; /* Largura fixa para os cards no carrossel */
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: transform 0.3s;
}

.product-card:hover {
  transform: translateY(-5px);
}

.card-image {
  width: 100%;
  height: 380px; /* Altura fixa para todas as capas */
  background: #f5f5f5;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover; /* Garante que a capa preencha o espaço sem deformar */
}

.card-info {
  padding: 15px;
  text-align: center;
}

.product-title {
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 8px;
  /* Garante que títulos longos não quebrem o layout */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  height: 2.8em;
}

.product-price {
  font-weight: bold;
  color: #2c3e50;
  font-size: 1.2rem;
}

/* Garante que o contêiner pai seja a referência para os botões */
.carousel-wrapper {
  position: relative; /* FUNDAMENTAL para os botões funcionarem */
  display: flex;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 50px; /* Cria espaço nas laterais para os botões não ficarem em cima dos cards */
}

/* Estilização robusta dos botões de navegação */
.nav-btn {
  position: absolute; /* Move o botão para as laterais do wrapper */
  top: 50%;           /* Centraliza verticalmente */
  transform: translateY(-50%);
  background: #ffffff;
  color: #333;
  border: none;
  width: 50px;        /* Tamanho maior para facilitar o clique */
  height: 50px;
  border-radius: 50%;
  box-shadow: 0 4px 10px rgba(0,0,0,0.2);
  cursor: pointer;
  font-size: 2rem;    /* Aumenta o símbolo < e > */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;       /* Garante que fique acima das imagens */
  transition: all 0.3s ease;
}

.nav-btn:hover {
  background: #f0f0f0;
  transform: translateY(-50%) scale(1.1);
}

/* Posicionamento exato */
.prev {
  left: 0;
}

.next {
  right: 0;
}


</style>